import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { getApplications, GetApplicationsResult, APPLICATION_STATUS_LABELS, ApplicationStatus } from "@/lib/api/applications";
import { cn } from "@/lib/utils";

// Feature flag for new application
const isNewApplicationEnabled = import.meta.env.VITE_FEATURE_NEW_APPLICATION === "true";

const ITEMS_PER_PAGE = 9;

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
    </svg>
  );
}

function FileIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
    </svg>
  );
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
    </svg>
  );
}

function EditIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" />
    </svg>
  );
}

function EyeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  );
}

function ChevronLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
    </svg>
  );
}

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
    </svg>
  );
}

function FilterIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
    </svg>
  );
}

// Custom Dropdown Component
interface DropdownOption<T extends string> {
  value: T;
  label: string;
}

interface CustomDropdownProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: DropdownOption<T>[];
  placeholder?: string;
  className?: string;
}

function CustomDropdown<T extends string>({ value, onChange, options, className }: CustomDropdownProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const selectedOption = options.find(opt => opt.value === value);
  
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  
  return (
    <div ref={dropdownRef} className={cn("relative", className)}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full h-10 px-3 pr-8 rounded-lg border-2 border-border/50 bg-white text-[13px] text-left focus:outline-none focus:border-primary/50 transition-colors flex items-center justify-between"
      >
        <span className={selectedOption ? "text-foreground" : "text-muted-foreground"}>
          {selectedOption?.label || "Виберіть..."}
        </span>
        <ChevronDownIcon className={cn(
          "absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground transition-transform",
          isOpen && "rotate-180"
        )} />
      </button>
      
      {isOpen && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 py-1 bg-white rounded-lg border border-border shadow-lg max-h-60 overflow-auto">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              className={cn(
                "w-full px-3 py-2 text-[13px] text-left hover:bg-muted/50 flex items-center justify-between transition-colors",
                option.value === value && "bg-primary/5 text-primary font-medium"
              )}
            >
              <span>{option.label}</span>
              {option.value === value && <CheckIcon className="h-4 w-4" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function getStatusColor(status: string): string {
  switch (status) {
    case "draft": return "bg-slate-100 text-slate-700";
    case "submitted": return "bg-blue-100 text-blue-700";
    case "under_review": return "bg-amber-100 text-amber-700";
    case "documents_requested": return "bg-orange-100 text-orange-700";
    case "approved": return "bg-green-100 text-green-700";
    case "rejected": return "bg-red-100 text-red-700";
    case "order_issued": return "bg-indigo-100 text-indigo-700";
    case "payment_pending": return "bg-violet-100 text-violet-700";
    case "payment_sent": return "bg-cyan-100 text-cyan-700";
    case "payment_received": return "bg-teal-100 text-teal-700";
    case "in_progress": return "bg-lime-100 text-lime-700";
    case "report_pending": return "bg-yellow-100 text-yellow-700";
    case "report_submitted": return "bg-sky-100 text-sky-700";
    case "report_review": return "bg-purple-100 text-purple-700";
    case "report_approved": return "bg-emerald-100 text-emerald-700";
    case "completed": return "bg-emerald-100 text-emerald-700";
    case "funds_returned": return "bg-rose-100 text-rose-700";
    case "cancelled": return "bg-gray-100 text-gray-700";
    default: return "bg-slate-100 text-slate-700";
  }
}

function formatDate(dateString: string | null): string {
  if (!dateString) return "—";
  return new Date(dateString).toLocaleDateString("uk-UA", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

// Status filter options
const STATUS_FILTER_OPTIONS: DropdownOption<ApplicationStatus | "all">[] = [
  { value: "all", label: "Всі статуси" },
  { value: "draft", label: "Чернетки" },
  { value: "submitted", label: "Подано" },
  { value: "under_review", label: "На розгляді" },
  { value: "approved", label: "Схвалено" },
  { value: "rejected", label: "Відхилено" },
  { value: "payment_received", label: "Кошти отримано" },
  { value: "in_progress", label: "Виконується" },
  { value: "completed", label: "Завершено" },
];

// Date filter options
const DATE_FILTER_OPTIONS: DropdownOption<string>[] = [
  { value: "all", label: "Весь час" },
  { value: "today", label: "Сьогодні" },
  { value: "week", label: "Цей тиждень" },
  { value: "month", label: "Цей місяць" },
  { value: "quarter", label: "Цей квартал" },
];

// Pagination component
interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  totalItems: number;
  itemsPerPage: number;
}

function Pagination({ currentPage, totalPages, onPageChange, totalItems, itemsPerPage }: PaginationProps) {
  if (totalPages <= 1) return null;
  
  // Calculate visible page numbers
  const getPageNumbers = () => {
    const pages: (number | "ellipsis")[] = [];
    const maxVisible = 7;
    
    if (totalPages <= maxVisible) {
      // Show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show first, last, current and neighbors with ellipsis
      pages.push(1);
      
      if (currentPage > 3) {
        pages.push("ellipsis");
      }
      
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) {
          pages.push(i);
        }
      }
      
      if (currentPage < totalPages - 2) {
        pages.push("ellipsis");
      }
      
      if (!pages.includes(totalPages)) {
        pages.push(totalPages);
      }
    }
    
    return pages;
  };
  
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);
  
  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-3 border-t border-border/50">
      <p className="text-[12px] text-muted-foreground">
        Показано {startItem}–{endItem} з {totalItems} заявок
      </p>
      
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="h-8 w-8 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeftIcon className="h-4 w-4" />
        </button>
        
        {getPageNumbers().map((page, index) => (
          page === "ellipsis" ? (
            <span key={`ellipsis-${index}`} className="h-8 w-8 flex items-center justify-center text-muted-foreground text-[13px]">
              ...
            </span>
          ) : (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              className={cn(
                "h-8 w-8 rounded-lg flex items-center justify-center text-[13px] font-medium transition-colors",
                page === currentPage
                  ? "bg-primary text-white"
                  : "text-muted-foreground hover:bg-muted"
              )}
            >
              {page}
            </button>
          )
        ))}
        
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="h-8 w-8 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronRightIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export function Applications() {
  const [result, setResult] = useState<GetApplicationsResult>({
    data: [],
    total: 0,
    page: 1,
    limit: ITEMS_PER_PAGE,
    totalPages: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [statusFilter, setStatusFilter] = useState<ApplicationStatus | "all">("all");
  const [dateFilter, setDateFilter] = useState<"all" | "today" | "week" | "month" | "quarter">("all");
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const loadApplications = useCallback(async (page: number) => {
    setIsLoading(true);
    const data = await getApplications({
      page,
      limit: ITEMS_PER_PAGE,
      status: statusFilter,
      search: searchQuery,
      dateFilter,
    });
    setResult(data);
    setCurrentPage(page);
    setIsLoading(false);
  }, [statusFilter, searchQuery, dateFilter]);

  // Load on mount and when filters change
  useEffect(() => {
    loadApplications(1);
  }, [loadApplications]);

  // Debounced search
  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      setSearchQuery(value);
    }, 300);
  };

  const handlePageChange = (page: number) => {
    loadApplications(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const activeFiltersCount = (statusFilter !== "all" ? 1 : 0) + (dateFilter !== "all" ? 1 : 0);

  return (
    <AppLayout userRole="applicant">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold tracking-tight">Мої заявки</h1>
            <p className="text-[13px] text-muted-foreground mt-0.5">
              Перегляд та керування заявками
            </p>
          </div>
          {isNewApplicationEnabled ? (
            <Button asChild size="lg">
              <Link to="/application/new" className="inline-flex flex-row items-center gap-1.5">
                <PlusIcon className="h-4 w-4" />
                Нова заявка
              </Link>
            </Button>
          ) : (
            <Button disabled size="lg" className="opacity-60 cursor-not-allowed">
              <PlusIcon className="h-4 w-4 mr-1.5" />
              Скоро
            </Button>
          )}
        </div>

        {/* Search and Filter Button */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Пошук за адресою, номером заявки..."
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full h-10 pl-10 pr-4 rounded-xl border-[3px] border-border/50 bg-white text-[13px] placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={cn(
              "relative inline-flex items-center justify-center h-10 w-10 rounded-xl border-[3px] transition-colors shrink-0",
              showFilters || activeFiltersCount > 0
                ? "border-primary/50 bg-primary/5 text-primary"
                : "border-border/50 bg-white text-muted-foreground hover:border-border"
            )}
            title="Фільтри"
          >
            <FilterIcon className="h-4 w-4" />
            {activeFiltersCount > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-primary text-white text-[9px] rounded-full flex items-center justify-center">
                {activeFiltersCount}
              </span>
            )}
          </button>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="p-4 bg-white rounded-xl border-[3px] border-border/50">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
                  Статус
                </label>
                <CustomDropdown
                  value={statusFilter}
                  onChange={setStatusFilter}
                  options={STATUS_FILTER_OPTIONS}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
                  Період
                </label>
                <CustomDropdown
                  value={dateFilter}
                  onChange={(v) => setDateFilter(v as typeof dateFilter)}
                  options={DATE_FILTER_OPTIONS}
                />
              </div>
            </div>
            {activeFiltersCount > 0 && (
              <div className="mt-3 pt-3 border-t border-border/50">
                <button
                  onClick={() => {
                    setStatusFilter("all");
                    setDateFilter("all");
                  }}
                  className="text-[13px] text-primary hover:text-primary/80 font-medium"
                >
                  Скинути фільтри
                </button>
              </div>
            )}
          </div>
        )}

        {/* List */}
        <div className="bg-white rounded-2xl border-[3px] border-border/50">
          {isLoading ? (
            <div className="py-16 flex items-center justify-center">
              <div className="flex flex-col items-center justify-center">
                <Spinner size="lg" />
                <p className="mt-4 text-muted-foreground">Завантаження заявок...</p>
              </div>
            </div>
          ) : result.data.length > 0 ? (
            <>
              <div className="divide-y divide-border/50">
                {result.data.map((app) => {
                  const isDraft = app.status === "draft";
                  const linkPath = isDraft ? `/application/${app.id}` : `/application/${app.id}/view`;
                  
                  return (
                    <Link
                      key={app.id}
                      to={linkPath}
                      className="flex items-center gap-4 p-4 hover:bg-muted/50 transition-colors"
                    >
                      <div className={cn(
                        "h-10 w-10 rounded-xl flex items-center justify-center shrink-0",
                        isDraft ? "bg-primary/10" : "bg-slate-100"
                      )}>
                        <FileIcon className={cn("h-5 w-5", isDraft ? "text-primary" : "text-slate-500")} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-[14px] truncate">
                            {app.application_number || `Заявка #${app.user_seq || app.id}`}
                          </span>
                          <span className={cn(
                            "px-2 py-0.5 rounded-full text-[11px] font-medium shrink-0",
                            getStatusColor(app.status)
                          )}>
                            {APPLICATION_STATUS_LABELS[app.status] || app.status}
                          </span>
                        </div>
                        <p className="text-[13px] text-muted-foreground truncate mt-0.5">
                          {app.building_address || "Адреса не вказана"}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-[12px] text-muted-foreground">
                          {formatDate(app.date_created)}
                        </p>
                        {isDraft ? (
                          <span className="inline-flex items-center gap-1 text-[11px] text-primary mt-1">
                            <EditIcon className="h-3 w-3" />
                            Редагувати
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[11px] text-slate-500 mt-1">
                            <EyeIcon className="h-3 w-3" />
                            Переглянути
                          </span>
                        )}
                      </div>
                    </Link>
                  );
                })}
              </div>
              
              {/* Pagination */}
              <Pagination
                currentPage={currentPage}
                totalPages={result.totalPages}
                onPageChange={handlePageChange}
                totalItems={result.total}
                itemsPerPage={ITEMS_PER_PAGE}
              />
            </>
          ) : (
            <div className="py-16 text-center">
              <div className="h-14 w-14 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
                <FileIcon className="h-7 w-7 text-muted-foreground/50" />
              </div>
              {searchQuery || statusFilter !== "all" || dateFilter !== "all" ? (
                <>
                  <h3 className="font-medium text-[15px]">Заявок не знайдено</h3>
                  <p className="text-[13px] text-muted-foreground mt-1 mb-5">
                    Спробуйте змінити параметри пошуку
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSearchInput("");
                      setSearchQuery("");
                      setStatusFilter("all");
                      setDateFilter("all");
                    }}
                  >
                    Скинути фільтри
                  </Button>
                </>
              ) : (
                <>
                  <h3 className="font-medium text-[15px]">У вас ще немає заявок</h3>
                  <p className="text-[13px] text-muted-foreground mt-1 mb-5">
                    {isNewApplicationEnabled 
                      ? "Створіть першу заявку на допомогу" 
                      : "Функція подання заявок буде доступна найближчим часом"}
                  </p>
                  {isNewApplicationEnabled ? (
                    <Button asChild>
                      <Link to="/application/new" className="inline-flex flex-row items-center gap-1.5">
                        <PlusIcon className="h-4 w-4" />
                        Створити заявку
                      </Link>
                    </Button>
                  ) : (
                    <Button disabled className="opacity-60 cursor-not-allowed">
                      <PlusIcon className="h-4 w-4 mr-1.5" />
                      Скоро
                    </Button>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}

export default Applications;
